import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Table, Pagination, Spin, Button} from 'antd';
import {MINI_FEEDBACK_RESPONSES} from '../../resolvers';
import moment from 'moment';
import DBModal from 'components/modal/modal';
import styles from '../singleMiniFeedback.module.scss';
import {Avatar} from 'antd';
import {UserOutlined, ExportOutlined} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {ExportResponsesForm} from '../components/ExportResponsesForm';

export const ResponsesTab = ({feedbackId, featureId}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [openExport, setOpenExport] = useState(false);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.FAQ),
  );

  const {data, loading, refetch} = useQuery(MINI_FEEDBACK_RESPONSES, {
    variables: {
      input: {
        feedbackId,
        pagination: {
          page: currentPage,
          limit: limit,
        },
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const handlePageChange = page => {
    setCurrentPage(page);
    refetch({
      input: {
        feedbackId,
        pagination: {
          page,
          limit,
        },
      },
    });
  };

  const onShowSizeChange = (current, size) => {
    setLimit(size);
    setCurrentPage(1);
    refetch({
      input: {
        feedbackId,
        pagination: {
          page: 1,
          limit: size,
        },
      },
    });
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => moment(text).format('MMM DD, YYYY HH:mm'),
    },
    {
      title: 'User Name',
      key: 'user_name',
      render: (_, record) => {
        const user = record.user;
        if (!user)
          return (
            <span style={{color: '#9ca3af', fontStyle: 'italic'}}>
              Anonymous
            </span>
          );

        return (
          <div
            style={{display: 'flex', alignItems: 'center', gap: 10}}
            onClick={e => e.stopPropagation()} // Prevent opening details modal if clicking name
          >
            <Avatar
              src={user.imageUrl}
              icon={!user.imageUrl && <UserOutlined />}
            />
            <a
              href={`/dashboard/user/${user.id}`}
              target="_blank"
              rel="noreferrer"
              style={{
                color: '#111827',
                fontWeight: 500,
                textDecoration: 'underline',
              }}>
              {`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}
            </a>
          </div>
        );
      },
    },
    {
      title: 'Username',
      key: 'username',
      render: (_, record) => {
        const user = record.user;
        if (!user)
          return (
            <span style={{color: '#9ca3af', fontStyle: 'italic'}}>
              Anonymous
            </span>
          );
        return user.username || '-';
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => {
        const user = record.user;
        if (!user)
          return (
            <span style={{color: '#9ca3af', fontStyle: 'italic'}}>
              Anonymous
            </span>
          );
        return user.email || '-';
      },
    },
    {
      title: 'Total Answers',
      key: 'answers',
      render: (_, record) => record.answers?.length || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <a
          style={{color: '#8b5cf6', fontWeight: 500}}
          onClick={e => {
            e.stopPropagation();
            setSelectedResponse(record);
          }}>
          View Details
        </a>
      ),
    },
  ];

  const responseData = data?.getMiniFeedbackResponses;

  if (loading && !responseData) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  if (responseData?.__typename === 'Error') {
    return (
      <div className={styles.error}>
        <h3>{responseData.message}</h3>
      </div>
    );
  }

  const responses = responseData?.data || [];
  const total = responseData?.pagination?.total || 0;

  return (
    <div style={{padding: '0 8px'}}>
      {tabOperations?.export && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            className={styles.primaryBtn}
            onClick={() => setOpenExport(true)}>
            Export Responses
          </Button>
        </div>
      )}
      <Table
        dataSource={responses}
        columns={columns}
        pagination={false}
        rowKey="id"
        onRow={record => ({
          onClick: () => {
            setSelectedResponse(record);
          },
          style: {cursor: 'pointer'},
        })}
      />
      {total > 0 && (
        <Pagination
          showQuickJumper
          current={currentPage}
          showSizeChanger
          onChange={handlePageChange}
          total={total}
          pageSize={limit}
          onShowSizeChange={onShowSizeChange}
          style={{marginTop: 20, textAlign: 'right'}}
        />
      )}

      {tabOperations?.export && (
        <DBModal
          isOpen={openExport}
          handleClose={() => setOpenExport(false)}
          destroyOnClose
          width={520}
          content={
            <ExportResponsesForm
              feedbackId={feedbackId}
              featureId={featureId}
              closeModal={() => setOpenExport(false)}
            />
          }
        />
      )}

      {selectedResponse && (
        <DBModal
          isOpen={true}
          handleClose={() => setSelectedResponse(null)}
          width={700}
          content={
            <div
              style={{maxHeight: '70vh', overflowY: 'auto', paddingRight: 10}}>
              <h1 style={{fontSize: 24, marginBottom: 20}}>Response Details</h1>

              <div
                style={{
                  background: '#f9fafb',
                  padding: '20px',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  marginBottom: 24,
                }}>
                <h3
                  style={{
                    margin: 0,
                    paddingBottom: 16,
                    borderBottom: '1px solid #e5e7eb',
                    marginBottom: 16,
                  }}>
                  User Information
                </h3>
                {selectedResponse.user ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '20px',
                    }}>
                    <div>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: 13,
                          marginBottom: 4,
                        }}>
                        Name
                      </div>
                      <div style={{fontWeight: 500}}>
                        {selectedResponse.user.firstName}{' '}
                        {selectedResponse.user.lastName}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: 13,
                          marginBottom: 4,
                        }}>
                        Email
                      </div>
                      <div style={{fontWeight: 500}}>
                        {selectedResponse.user.email}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: 13,
                          marginBottom: 4,
                        }}>
                        Username
                      </div>
                      <div style={{fontWeight: 500}}>
                        {selectedResponse.user.username}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      color: '#6b7280',
                      fontStyle: 'italic',
                      padding: '8px 0',
                    }}>
                    This feedback was submitted anonymously.
                  </div>
                )}
              </div>

              <div
                style={{
                  background: '#ffffff',
                  padding: '24px',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                }}>
                <h3
                  style={{
                    margin: 0,
                    marginBottom: 20,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <span>Answers</span>
                  <span
                    style={{
                      fontSize: 14,
                      color: '#6b7280',
                      fontWeight: 'normal',
                    }}>
                    Submitted:{' '}
                    {moment(selectedResponse.createdAt).format('MMM DD, YYYY')}
                  </span>
                </h3>

                <div
                  style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                  {selectedResponse.answers?.map((ans, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px',
                        background: '#f3f4f6',
                        borderRadius: 8,
                      }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: 10,
                          fontSize: 15,
                        }}>
                        <span style={{color: '#8b5cf6', marginRight: 8}}>
                          Q{idx + 1}.
                        </span>
                        {ans.questionText || `Question ID: ${ans.questionId}`}
                      </div>
                      <div
                        style={{
                          color: '#374151',
                          padding: '12px',
                          background: '#ffffff',
                          borderRadius: 6,
                          border: '1px solid #e5e7eb',
                        }}>
                        {Array.isArray(ans.answer)
                          ? ans.answer
                              .map(a =>
                                typeof a === 'object'
                                  ? a?.label || JSON.stringify(a)
                                  : a,
                              )
                              .join(', ')
                          : typeof ans.answer === 'object' &&
                            ans.answer !== null
                          ? ans.answer.label || JSON.stringify(ans.answer)
                          : String(ans.answer || '-')}
                      </div>
                    </div>
                  ))}

                  {(!selectedResponse.answers ||
                    selectedResponse.answers.length === 0) && (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '30px 0',
                        color: '#6b7280',
                      }}>
                      No answers recorded for this response.
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: 30,
                }}>
                <Button
                  type="primary"
                  onClick={() => setSelectedResponse(null)}
                  style={{
                    background: '#8b5cf6',
                    borderColor: '#8b5cf6',
                    borderRadius: 8,
                    height: 40,
                    padding: '0 24px',
                  }}>
                  Close
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};
